import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Clock, ArrowRight, Package, Star, Sparkles } from 'lucide-react';
import axios from 'axios';

const API_URL = '/api';

// Rehabilitation packages (same as main page)
const PACKAGES = [
  {
    id: 'paket-5',
    name: 'Paket 5 obravnav',
    sessions: 5,
    price: 360,
    originalPrice: 400,
    validity: '3 mesece',
    featured: false,
  },
  {
    id: 'paket-10',
    name: 'Paket 10 obravnav',
    sessions: 10,
    price: 680,
    originalPrice: 800,
    validity: '6 mesecev',
    featured: true,
  },
  {
    id: 'paket-15',
    name: 'Paket 15 obravnav',
    sessions: 15,
    price: 960,
    originalPrice: 1200,
    validity: '12 mesecev',
    featured: false,
  }
];

const PricingModal = ({ isOpen, onClose, onBookService }) => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('storitve');

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API_URL}/services`);
        setServices(response.data);
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoading(false);
      }
    };
    if (isOpen) fetchServices();
  }, [isOpen]);

  const handleBookService = (service) => {
    onClose();
    setTimeout(() => {
      onBookService(service);
    }, 200);
  };

  const handleBookPackage = (pkg) => {
    const serviceObj = {
      id: pkg.id,
      name: pkg.name,
      price: pkg.price,
      duration: pkg.sessions * 45
    };
    onClose();
    setTimeout(() => {
      onBookService(serviceObj);
    }, 200);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden rounded-2xl bg-white max-h-[85vh]">
        {/* Header */}
        <div className="bg-[#1a365d] px-6 py-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">Cenik</h2>
              <p className="text-white/70 text-sm">Izberite storitev ali paket</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-4 sm:px-6 pt-4">
          <div className="flex bg-slate-100 rounded-full p-1">
            <button
              onClick={() => setActiveTab('storitve')}
              className={`flex-1 py-2 px-4 rounded-full text-sm font-semibold transition-all ${
                activeTab === 'storitve'
                  ? 'bg-white text-[#1a365d] shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Storitve
            </button>
            <button
              onClick={() => setActiveTab('paketi')}
              className={`flex-1 py-2 px-4 rounded-full text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
                activeTab === 'paketi'
                  ? 'bg-white text-[#1a365d] shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <Package className="w-4 h-4" />
              Paketi
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 overflow-y-auto max-h-[60vh]">
          {activeTab === 'storitve' && (
            <>
              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-20 bg-slate-100 rounded-xl animate-pulse" />
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  {services
                    .filter(service => !service.name.toLowerCase().includes('paket'))
                    .map((service) => (
                    <div
                      key={service.id}
                      className="bg-slate-50 hover:bg-slate-100 rounded-xl p-4 transition-all group cursor-pointer border border-transparent hover:border-[#f87171]/30"
                      onClick={() => handleBookService(service)}
                      data-testid={`pricing-modal-service-${service.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h3 className="font-semibold text-[#1a365d] group-hover:text-[#f87171] transition-colors">
                            {service.name}
                          </h3>
                          <div className="flex items-center gap-2 text-sm text-slate-500 mt-1">
                            <Clock className="w-4 h-4" />
                            <span>{service.duration} min</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <span className="text-2xl font-bold text-[#1a365d]">{service.price}</span>
                            <span className="text-slate-500 ml-1">€</span>
                          </div>
                          <div className="w-10 h-10 bg-[#f87171] rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <ArrowRight className="w-5 h-5 text-white" />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {activeTab === 'paketi' && (
            <div className="space-y-3">
              {PACKAGES.map((pkg) => (
                <div
                  key={pkg.id}
                  className={`bg-slate-50 hover:bg-slate-100 rounded-xl p-4 transition-all group cursor-pointer border-2 ${
                    pkg.featured ? 'border-[#f87171]' : 'border-transparent hover:border-[#f87171]/30'
                  }`}
                  onClick={() => handleBookPackage(pkg)}
                  data-testid={`pricing-modal-package-${pkg.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-[#1a365d] group-hover:text-[#f87171] transition-colors">
                          {pkg.name}
                        </h3>
                        {pkg.featured && (
                          <span className="bg-[#f87171] text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                            <Star className="w-3 h-3 fill-white" />
                            Priljubljen
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-sm text-slate-500 mt-1">
                        <span className="flex items-center gap-1">
                          <Package className="w-4 h-4" />
                          {pkg.sessions} obravnav
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {pkg.validity}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <span className="text-2xl font-bold text-[#1a365d]">{pkg.price}</span>
                        <span className="text-slate-500 ml-1">€</span>
                        <div className="text-xs text-slate-400 line-through">{pkg.originalPrice} €</div>
                      </div>
                      <div className="w-10 h-10 bg-[#f87171] rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <ArrowRight className="w-5 h-5 text-white" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Savings info */}
              <div className="bg-green-50 rounded-xl p-4 flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-green-600" />
                <p className="text-sm text-green-700">
                  <strong>Prihranite do 20%</strong> z nakupom paketa obravnav!
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t">
          <Button
            variant="outline"
            onClick={onClose}
            className="w-full rounded-full"
          >
            Zapri
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PricingModal;
